import { cn } from "@newxsystems/shared";
import type { ActionStyle } from "@newxsystems/types";
import type { AnchorHTMLAttributes } from "react";
import { getComponent } from "../../../../client";
import type { IHeroContentProps } from "./HeroContent.props";

const buttonClasses: Record<ActionStyle, string> = {
  primary:
    "border border-foreground bg-foreground text-background hover:border-electric-blue hover:bg-electric-blue hover:text-white",
  secondary:
    "border border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background",
  ghost: "border border-transparent text-foreground hover:border-foreground",
  destructive: "border border-destructive bg-destructive text-white hover:bg-destructive-strong",
};

export const HeroContent = ({
  alignment,
  buttons,
  compact,
  description,
  eyebrow,
  heading,
  inverse,
}: IHeroContentProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");
  const isCentered = alignment === "center";

  if (!eyebrow && !heading && !description && !buttons?.length) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex min-w-0 max-w-[720px] flex-col",
        isCentered && "mx-auto items-center text-center",
        inverse ? "text-white" : "text-foreground",
      )}
    >
      {eyebrow ? <p className="mb-5 font-mono text-[12px]/[1.45]">{eyebrow}</p> : null}
      {heading ? (
        <h1
          className={cn(
            "max-w-[14ch] text-balance font-semibold tracking-[-0.035em] [overflow-wrap:anywhere]",
            compact ? "text-[44px]/[0.98] md:text-[56px]" : "text-[48px]/[0.96] md:text-[76px]",
          )}
        >
          {heading}
        </h1>
      ) : null}
      {description ? (
        <div
          className={cn(
            "mt-7 max-w-[62ch] text-pretty text-[16px]/[1.55] md:text-[17px] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc",
            inverse ? "text-white/90 [&_a]:text-white" : "text-onSurface-500",
          )}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      ) : null}
      {buttons?.length && Link ? (
        <div className={cn("mt-8 flex flex-wrap gap-3", isCentered && "justify-center")}>
          {buttons.map((button, index) => {
            const style = button.style ?? "primary";
            const inverseClass = inverse
              ? style === "primary"
                ? "border-white bg-white text-foreground hover:border-electric-blue hover:bg-electric-blue hover:text-white"
                : "border-white text-white hover:bg-white hover:text-foreground"
              : buttonClasses[style];

            return (
              <Link
                aria-label={button.accessibilityLabel ?? button.label}
                className={cn(
                  "inline-flex min-h-12 min-w-0 items-center justify-center rounded-full border px-5 text-center font-mono text-[12px]/[1.45] transition-colors duration-200 [overflow-wrap:anywhere] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring",
                  inverseClass,
                )}
                href={button.href}
                key={`${button.href}-${index}`}
                rel={button.openInNewTab ? "noreferrer" : undefined}
                target={button.openInNewTab ? "_blank" : undefined}
              >
                {button.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

HeroContent.displayName = "Hero::HeroContent";
